import React from 'react';
import styled from 'styled-components';
import { useBlogDetail } from './hook';
import { useParams } from 'react-router-dom';
import { Link } from '../../atoms/Link';
import { ShareButton } from '../../atoms/ShareButton';
import { COLOR_PRIMARY } from '../../../constants/color';
import { buildImageUrl } from '../../../utils/imageUrl';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SEO } from '../../atoms/SEO';
import dayjs from 'dayjs';

export const BlogDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { blog, loading, error } = useBlogDetail(id ?? "");
  const { t } = useLanguage();
  const seoDescription = blog?.content
    ? blog.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160)
    : undefined;

  return (
    <PageWrapper>
      <ContentWrapper>
        {!loading && blog && (
          <SEO
            title={blog.title}
            description={seoDescription}
            image={blog.image ? buildImageUrl(blog.image) : undefined}
            url={window.location.href}
            type="article"
          />
        )}
        <BackLink to="/blog">{t('blog.back')}</BackLink>
        {loading && <LoadingMsg>{t('common.loading')}</LoadingMsg>}
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!loading && blog && (
          <>
            <TitleRow>
              <Title>{blog.title}</Title>
              {id && (
                <ShareButton
                  propertyId={id}
                  iconOnly
                  path={`/blog/${id}`}
                />
              )}
            </TitleRow>
            <MetaRow>
              {blog.author_name && (
                <Author>{t('blog.author')} {blog.author_name}</Author>
              )}
              <DateText>{dayjs(blog.created_at as string).format('DD MMMM YYYY')}</DateText>
            </MetaRow>
            {blog.image && (
              <ImageWrapper>
                <Image src={buildImageUrl(blog.image)} alt={blog.title} />
              </ImageWrapper>
            )}
            <ContentBlock>
              <Content dangerouslySetInnerHTML={{ __html: blog.content }} />
            </ContentBlock>
          </>
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  margin-top: 20px;
  
  @media (min-width: 768px) {
    margin-top: 30px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 12px 40px;
  width: 100%;
  background: #ffffff;
  
  @media (min-width: 400px) {
    padding: 20px 16px 50px;
  }
  
  @media (min-width: 768px) {
    padding: 24px 20px 60px;
  }
  
  @media (min-width: 1024px) {
    padding: 30px 24px 80px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  color: #183153;
  font-size: 14px;
  font-weight: 500;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 12px;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    text-decoration: none;
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }
  
  /* Custom arrow icon */
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-left: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }
  
  &:hover::before {
    transform: rotate(40deg) translateX(-2px);
  }
  
  @media (min-width: 768px) {
    font-size: 15px;
    margin-bottom: 18px;
    padding: 10px 16px;
    gap: 8px;
    
    &::before {
      width: 10px;
      height: 10px;
    }
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    font-size: 24px;
  }
  
  @media (min-width: 1024px) {
    font-size: 26px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  color: #888;
  font-size: 13px;
  border-bottom: 1px solid ${COLOR_PRIMARY};
  padding-bottom: 12px;
  
  @media (min-width: 768px) {
    font-size: 14px;
    gap: 0;
  }
`;

const Author = styled.div`
  margin-bottom: 0;
`;

const DateText = styled.div`
  margin-bottom: 0;
  margin-left: auto;
  align-self: flex-end;
  text-align: right;

  @media (min-width: 768px) {
    align-self: auto;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  margin: 0 auto 20px auto;
  border-radius: 8px;
  overflow: hidden;
  background: #eee;
  
  @media (min-width: 768px) {
    height: 300px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 1024px) {
    height: 400px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const ContentBlock = styled.div`
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    margin-bottom: 24px;
  }
`;



const Content = styled.div`
  font-size: 14px;
  color: #222;
  margin-bottom: 8px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 15px;
  }
  
  /* Responsive content styling */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 20px;
    margin-bottom: 12px;
    line-height: 1.3;
  }
  
  p {
    margin-bottom: 16px;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 16px 0;
  }
  
  ul, ol {
    margin-bottom: 16px;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  blockquote {
    border-left: 4px solid ${COLOR_PRIMARY};
    padding-left: 16px;
    margin: 16px 0;
    font-style: italic;
    color: #666;
  }
`;

const LoadingMsg = styled.div`
  color: #888;
  margin: 20px 0;
  text-align: center;
  
  @media (min-width: 768px) {
    margin: 24px 0;
  }
`;

const ErrorMsg = styled.div`
  color: #d32f2f;
  margin: 20px 0;
  text-align: center;
  
  @media (min-width: 768px) {
    margin: 24px 0;
  }
`;
